import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8vevprh5tg6sock1j0";

const values = [
  {
    icon: "ri-book-open-line",
    title: "系统梳理剧集资料",
    desc: "把仙剑正传、续集、衍生与相关仙侠剧的资料集中呈现，减少到处查找的时间。",
  },
  {
    icon: "ri-user-star-line",
    title: "角色与剧情解读",
    desc: "为每一部作品整理剧情脉络与角色档案，帮助新观众快速入门，也方便老剧迷重温。",
  },
  {
    icon: "ri-newspaper-line",
    title: "持续更新的资讯",
    desc: "汇集剧情解读、角色访谈、幕后花絮与行业动态，让仙侠世界常看常新。",
  },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  useSeo({
    title: "关于本站 - 仙剑奇侠传影视站",
    description:
      "了解仙剑奇侠传影视站的定位、内容范围、版权与免责声明，并可通过留言表单与我们联系反馈。",
    keywords: "仙剑奇侠传影视站,关于本站,版权声明,仙侠影视站,联系留言",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("留言已提交，感谢你的反馈！");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus("submitting");
    setFeedback("正在提交，请稍候…");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("留言已提交，感谢你的反馈！我们会尽快查看。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(
          serverMsg && !isSpam
            ? `提交失败：${serverMsg}`
            : "提交失败，请检查填写内容后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">关于本站</span>
            </nav>
            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              关于本站
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              仙剑奇侠传影视站是一个专注仙剑系列及经典仙侠影视作品的内容展示站点，
              希望为剧迷提供清晰、友好的资料浏览体验。
            </p>
          </div>
        </div>

        <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-12">
            <div className="lg:col-span-2">
              <h2 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
                <span className="h-6 w-1 rounded-full bg-accent-500"></span>
                网站定位
              </h2>
              <p className="mt-5 text-sm leading-loose text-foreground-700 md:text-base md:leading-loose">
                本站围绕“仙剑奇侠传”这一经典仙侠品牌展开，收录仙剑正传、续集、衍生作品，
                并延伸至《陈情令》《三生三世十里桃花》等相关经典仙侠剧。我们希望通过结构化的
                剧集资料、剧情介绍、角色分析与资讯内容，帮助新观众快速入门，也陪伴老剧迷重温经典。
              </p>

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {values.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-xl`}></i>
                    </span>
                    <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>

              <h2 className="mt-12 flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
                <span className="h-6 w-1 rounded-full bg-accent-500"></span>
                版权与免责声明
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700">
                <p>
                  <strong className="text-foreground-900">内容性质：</strong>
                  本站为影视资料展示与功能演示站点，页面中的剧集介绍、角色资料与资讯内容均为演示数据，
                  不构成任何官方资料，也不代表任何作品方的立场。
                </p>
                <p>
                  <strong className="text-foreground-900">资源说明：</strong>
                  本站不提供任何影视视频的在线播放、下载或外链资源，播放相关页面仅为前端界面演示，
                  点击播放不会进行任何真实播放行为。
                </p>
                <p>
                  <strong className="text-foreground-900">版权归属：</strong>
                  站内涉及的剧集名称、角色名称等相关知识产权归原作品及权利方所有。
                  本站仅出于学习与欣赏目的进行介绍性展示，如相关权利方对内容有异议，可通过下方表单联系我们处理。
                </p>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-xl font-bold text-foreground-950">联系我们</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  有内容建议、合作意向或版权相关问题，欢迎留言，我们会尽快查看。
                </p>

                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex items-center gap-3 rounded-md bg-background-50 px-4 py-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line"></i>
                    </span>
                    <span className="text-foreground-800">contact@xianjian-demo.site</span>
                  </div>
                  <div className="flex items-center gap-3 rounded-md bg-background-50 px-4 py-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-time-line"></i>
                    </span>
                    <span className="text-foreground-800">周一至周五 10:00 - 18:00</span>
                  </div>
                </div>

                <form
                  id="about-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-6 flex flex-col gap-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="text-xs font-medium text-foreground-700">
                      称呼
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200/60"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="text-xs font-medium text-foreground-700">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复的邮箱"
                      className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200/60"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="text-xs font-medium text-foreground-700">
                      主题
                    </label>
                    <select
                      id="contact-subject"
                      name="subject"
                      className="mt-1.5 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200/60"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="版权相关">版权相关</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="text-xs font-medium text-foreground-700">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="请输入你的留言（不超过 500 字）"
                      className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200/60"
                    ></textarea>
                  </div>

                  <input
                    className="form-field-optional-offset"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className="ri-send-plane-line"></i>
                    {status === "submitting" ? "提交中…" : "提交留言"}
                  </button>

                  {status !== "idle" && feedback && status !== "submitting" && (
                    <p
                      className={`rounded-md px-3.5 py-2.5 text-sm ${
                        status === "success"
                          ? "bg-primary-100 text-primary-800"
                          : "bg-accent-100 text-accent-900"
                      }`}
                    >
                      {feedback}
                    </p>
                  )}
                </form>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}